import {
  Menu,
  MenuContent,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "@rosc/ui/v32/menu";
import { RecordDock } from "@rosc/ui/v32/record-button";
import {
  ChevronDown,
  ChevronLeft,
  Globe,
  Languages,
  Loader2,
  Mic,
  MoreHorizontal,
  Pencil,
  Upload,
} from "lucide-react";
import type { JSX, ReactNode } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { V3Button, V3IconButton } from "@/components/ui/v3/button";
import { V3Chip } from "@/components/ui/v3/chip";
import { V3ConfirmSheet } from "@/components/ui/v3/confirm-sheet";
import { V3Drawer } from "@/components/ui/v3/drawer";
import { V3SwitchRow } from "@/components/ui/v3/switch";
import {
  NoiseReduction,
  TranscriptionProviderName,
} from "@/hooks/transcriber/types";
import { useNoteLanguage } from "@/hooks/user/useNoteLanguage";
import { cn } from "@/lib/utils";
import { useRecordingConsentStore } from "@/store/recordingConsent.store";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import { RecordingConsentCheck } from "./RecordingConsentCheck";
import { useRecordingSetup } from "./useRecordingSetup";

// The chips above Start recording, the same pill as the template chip
// (RecorderTemplateChip) so the row reads as one.
const CHIP =
  "inline-flex h-10 min-w-0 max-w-full items-center gap-1.5 rounded-full bg-v3-panel px-3 font-v3 text-[13.5px] font-semibold text-v3-text shadow-v3-hairline-strong transition-colors hover:bg-v3-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring disabled:opacity-60 data-[state=open]:bg-v3-surface-2";
const MENU =
  "max-h-[min(60dvh,420px)] w-[min(300px,calc(100vw-32px))] overflow-y-auto";
const SAME = "same";

const ChipLabel = ({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}): JSX.Element => (
  <>
    {icon}
    <span className="min-w-0 truncate">{children}</span>
    <ChevronDown aria-hidden className="size-4 shrink-0 text-v3-text-3" />
  </>
);

/** Conversation (input) language, "Auto" included where it works. */
export const InputLanguageChip = ({
  setup,
}: {
  setup: ReturnType<typeof useRecordingSetup>;
}): JSX.Element => {
  const { t } = useTranslation();
  const { inputLanguageOptions, inputLanguageValue } = setup;
  const selected = inputLanguageOptions.find(
    (option) => option.value === inputLanguageValue,
  );
  const short =
    inputLanguageValue === "auto"
      ? t("newVisitV32.language.auto")
      : (selected?.label ?? inputLanguageValue);
  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          aria-label={t("newVisitV32.language.aria", {
            language: selected?.label ?? short,
          })}
          className={CHIP}
          data-testid="newvisit-language-chip"
          type="button"
        >
          <ChipLabel icon={<Globe aria-hidden className="size-4 shrink-0" />}>
            {short}
          </ChipLabel>
        </button>
      </MenuTrigger>
      <MenuContent align="start" className={MENU} side="top">
        <MenuLabel>{t("newVisitV32.language.menu")}</MenuLabel>
        <MenuRadioGroup
          value={inputLanguageValue}
          onValueChange={setup.setInputLanguage}
        >
          {inputLanguageOptions.map((option) => (
            <MenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
};

/**
 * The note language (account preference, as in Settings → Transcription):
 * "Same as conversation" follows the input language.
 */
export const NoteLanguageChip = ({
  inputLanguage,
}: {
  inputLanguage: string | undefined;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const {
    options,
    current,
    sameAsConversation,
    loading,
    saving,
    failed,
    chooseLanguage,
    chooseSameAsConversation,
    syncWithInput,
  } = useNoteLanguage();

  // While "Same as conversation" is on, follow the input language (as the
  // classic NoteLanguageSelect does).
  const synced = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!sameAsConversation || !options.length) return;
    if (synced.current === inputLanguage) return;
    synced.current = inputLanguage;
    void syncWithInput(inputLanguage);
  }, [sameAsConversation, inputLanguage, options.length, syncWithInput]);

  if (!options.length && !loading) return null;
  const label = current
    ? t("newVisitV32.noteLanguage.chip", { language: current.label })
    : t("noteLanguage.label");
  return (
    <div className="flex min-w-0 flex-col">
      <Menu>
        <MenuTrigger asChild>
          <button
            aria-label={
              current
                ? t("noteLanguage.indicator", { language: current.label })
                : t("noteLanguage.label")
            }
            className={CHIP}
            data-testid="newvisit-note-language-chip"
            disabled={loading || saving || !options.length}
            type="button"
          >
            <ChipLabel
              icon={
                saving ? (
                  <Loader2
                    aria-hidden
                    className="size-4 shrink-0 animate-spin"
                  />
                ) : (
                  <Languages aria-hidden className="size-4 shrink-0" />
                )
              }
            >
              {label}
            </ChipLabel>
          </button>
        </MenuTrigger>
        <MenuContent align="start" className={MENU} side="top">
          <MenuLabel>{t("noteLanguage.menuTitle")}</MenuLabel>
          <p className="px-3 pb-2 text-[12.5px] leading-[18px] text-v3-text-3">
            {t("noteLanguage.tooltip")}
          </p>
          <MenuRadioGroup
            value={
              sameAsConversation ? SAME : current ? String(current.id) : ""
            }
            onValueChange={(value) => {
              if (value === SAME) void chooseSameAsConversation(inputLanguage);
              else void chooseLanguage(Number(value));
            }}
          >
            <MenuRadioItem value={SAME}>
              {t("noteLanguage.sameAsConversation")}
            </MenuRadioItem>
            <MenuSeparator />
            {options.map((option) => (
              <MenuRadioItem key={option.id} value={String(option.id)}>
                {option.label}
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
        </MenuContent>
      </Menu>
      {failed && (
        <p className="mt-1 px-2 text-[12.5px] text-v3-rec-text" role="alert">
          {t("noteLanguage.saveFailed")}
        </p>
      )}
    </div>
  );
};

/** Only when there is a choice: this computer's mics and linked phones. */
const MicrophoneChip = ({
  setup,
}: {
  setup: ReturnType<typeof useRecordingSetup>;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const phones = setup.isRealtimeEnabled ? setup.mobileDevices : [];
  if (setup.mics.length + phones.length < 2) return null;
  const selected = setup.micDevice?.label;
  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          aria-label={t("newVisitV32.microphone.aria", {
            name: selected ?? t("newVisitV32.microphone.menu"),
          })}
          className={cn(CHIP, "max-w-[220px]")}
          data-testid="newvisit-mic-chip"
          disabled={setup.loadingMics}
          type="button"
        >
          <ChipLabel icon={<Mic aria-hidden className="size-4 shrink-0" />}>
            {selected ?? t("newVisitV32.microphone.menu")}
          </ChipLabel>
        </button>
      </MenuTrigger>
      <MenuContent align="start" className={MENU} side="top">
        <MenuLabel>{t("newVisitV32.microphone.menu")}</MenuLabel>
        <MenuRadioGroup
          value={setup.micDevice?.value}
          onValueChange={setup.setMicDevice}
        >
          {setup.mics.map((mic) => (
            <MenuRadioItem key={mic.value} value={mic.value}>
              <span className="min-w-0 truncate">{mic.label}</span>
            </MenuRadioItem>
          ))}
          {setup.isRealtimeEnabled && (
            <>
              <MenuSeparator />
              <MenuLabel>
                {t(
                  "components.advancedSettings.microphoneDevice.mobileGroupLabel",
                )}
              </MenuLabel>
              {setup.loadingDevices ? (
                <div
                  aria-label={t(
                    "components.advancedSettings.microphoneDevice.loading",
                  )}
                  className="flex justify-center py-2 text-v3-text-3"
                  role="status"
                >
                  <Loader2 aria-hidden className="size-4 animate-spin" />
                </div>
              ) : setup.deviceError ? (
                <div className="flex items-center gap-2 px-3 py-2" role="alert">
                  <p className="flex-1 text-[13px] text-v3-text-2">
                    {t("error.unknown")}
                  </p>
                  <V3Button
                    size="default"
                    variant="secondary"
                    onClick={() => {
                      void setup.refreshDevices();
                    }}
                  >
                    {t("common.retry")}
                  </V3Button>
                </div>
              ) : (
                setup.mobileDevices.map((phone) => (
                  <MenuRadioItem key={phone.value} value={phone.value}>
                    <span className="min-w-0 truncate">{phone.label}</span>
                  </MenuRadioItem>
                ))
              )}
            </>
          )}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
};

/**
 * Live transcription, speaker separation and the microphone options: the
 * classic sheet's toggles and "Advanced settings", in one small drawer.
 * Same store, same defaults. No transcription provider choice: the provider
 * is chosen on the server (server-side STT routing); the client keeps
 * sending its current default.
 */
const RecordingSettingsDrawer = ({
  open,
  onOpenChange,
  setup,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setup: ReturnType<typeof useRecordingSetup>;
}): JSX.Element => {
  const { t } = useTranslation();
  const id = useId();
  const {
    isRealtimeEnabled,
    saveIsRealtimeEnabled,
    diarize,
    saveDiarize,
    transcriptionProvider,
    transcriptionNoRealtimeProvider,
  } = useRecordingSettingsStore();
  // Speaker separation needs Deepgram (live English, or recorded audio).
  const isDeepgramSelected = isRealtimeEnabled
    ? transcriptionProvider === TranscriptionProviderName.DEEPGRAM
    : (transcriptionNoRealtimeProvider?.startsWith("deepgram/") ?? false);
  const distance: { value: NoiseReduction; label: string }[] = [
    { value: NoiseReduction.FAR_FIELD, label: t("newVisitV32.settings.far") },
    { value: NoiseReduction.NEAR_FIELD, label: t("newVisitV32.settings.near") },
  ];
  return (
    <V3Drawer
      footer={
        <V3Button
          className="w-full max-sm:h-12"
          variant="primary"
          onClick={() => onOpenChange(false)}
        >
          {t("common.done")}
        </V3Button>
      }
      open={open}
      testId="newvisit-settings"
      title={t("newVisitV32.settings.title")}
      onOpenChange={onOpenChange}
    >
      <div className="divide-y divide-v3-border px-5 pb-2">
        <V3SwitchRow
          checked={isRealtimeEnabled}
          hint={t("newVisitV32.settings.liveHint")}
          id={`${id}-live`}
          label={t("newVisitV32.settings.live")}
          testId="newvisit-live-switch"
          onCheckedChange={saveIsRealtimeEnabled}
        />
        {isDeepgramSelected && (
          <V3SwitchRow
            checked={diarize}
            hint={t("components.advancedSettings.optionDiarization.tooltip")}
            id={`${id}-speakers`}
            label={t("newVisitV32.settings.speakers")}
            testId="newvisit-speakers-switch"
            onCheckedChange={saveDiarize}
          />
        )}
        <fieldset className="py-3">
          <legend className="float-left w-full text-[15px] font-medium leading-5 text-v3-text">
            {t("newVisitV32.settings.distance")}
          </legend>
          <p className="clear-left pt-0.5 text-[13px] leading-[18px] text-v3-text-3">
            {t("components.advancedSettings.optionNoiseReduction.tooltip")}
          </p>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {distance.map((option) => (
              <V3Chip
                key={option.value}
                aria-pressed={setup.noiseReduction === option.value}
                onClick={() => setup.setNoiseReduction(option.value)}
              >
                {option.label}
              </V3Chip>
            ))}
          </div>
        </fieldset>
        <V3SwitchRow
          checked={setup.noiseSuppression}
          hint={t("components.advancedSettings.optionNoiseSuppression.tooltip")}
          id={`${id}-noise`}
          label={t("newVisitV32.settings.noiseSuppression")}
          onCheckedChange={setup.setNoiseSuppression}
        />
      </div>
    </V3Drawer>
  );
};

// Wide desktop (the workspace column has room): a quiet Properties rail
// with the visit's status, date and consent, read only, as in the
// prototype (s0-idle-1440). The pickers stay above Start recording.
const RAIL_QUERY = "(min-width: 1280px)";
const useWide = (enabled: boolean): boolean => {
  const query = (): boolean =>
    enabled &&
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(RAIL_QUERY).matches;
  const [wide, setWide] = useState(query);
  useEffect(() => {
    if (!enabled || typeof window.matchMedia !== "function") {
      setWide(false);
      return;
    }
    const media = window.matchMedia(RAIL_QUERY);
    const update = (): void => setWide(media.matches);
    update();
    media.addEventListener?.("change", update);
    return () => media.removeEventListener?.("change", update);
  }, [enabled]);
  return wide;
};

/**
 * The visit heading: with `onChange`, the optional title typed in place (the
 * placeholder reads "New visit", as in the prototype); otherwise plain text.
 */
export const VisitTitleHeading = ({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string;
  value?: string;
  onChange?: (title: string) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const id = useId();
  if (!onChange)
    return (
      <h2 className="font-v3-display text-[30px] font-bold leading-9 tracking-[-0.02em] text-v3-text">
        {placeholder}
      </h2>
    );
  // The field grows with its text (a hidden copy sizes it), so the pencil
  // sits right after "New visit" or the typed title: it reads as an
  // editable title, not a disabled one. Focus underlines it, as in the
  // prototype.
  const shown = value || placeholder;
  return (
    <div className="flex min-w-0 items-center gap-2">
      <label className="sr-only" htmlFor={id}>
        {t("visits.sidebar.title.label")}
      </label>
      <span className="inline-grid min-w-0 max-w-full font-v3-display text-[30px] font-bold leading-9 tracking-[-0.02em]">
        <span
          aria-hidden
          className="invisible col-start-1 row-start-1 overflow-hidden whitespace-pre pr-1"
        >
          {shown}
        </span>
        <input
          autoComplete="off"
          className="peer col-start-1 row-start-1 w-full min-w-0 rounded-none border-0 bg-transparent p-0 font-[inherit] text-v3-text outline-none transition-shadow placeholder:text-v3-text-3 focus-visible:shadow-[inset_0_-2px_0_var(--v3-brand)]"
          data-testid="newvisit-title-input"
          id={id}
          maxLength={128}
          placeholder={placeholder}
          size={1}
          type="text"
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value)}
        />
      </span>
      <label
        aria-hidden
        className="flex size-8 shrink-0 cursor-text items-center justify-center rounded-full text-v3-text-3 transition-colors hover:bg-v3-surface-2 hover:text-v3-text-2"
        htmlFor={id}
        title={t("visits.sidebar.title.label")}
      >
        <Pencil className="size-[18px]" />
      </label>
    </div>
  );
};

const RailRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element => (
  <div className="grid min-h-11 grid-cols-[104px_minmax(0,1fr)] items-center gap-3">
    <dt className="text-[13.5px] text-v3-text-3">{label}</dt>
    <dd className="min-w-0 text-[14px] text-v3-text">{children}</dd>
  </div>
);

/**
 * New visit before recording, in the v3.2 style (iOS RecordingView idle):
 * a top bar with Close and the recording settings, the title, the consent
 * chip and a short hint; at the bottom the language, note language,
 * template and microphone chips over one ink "Start recording" button (at
 * every width), with
 * "Upload a recording" always under it.
 */
export const NewVisitSetupV32 = ({
  onStart,
  onUpload,
  onClose,
  busy = false,
  disabled = false,
  title,
  uploadWithLive = false,
  consent = false,
  templateChip,
  visitTitle,
  onVisitTitleChange,
  properties = false,
  children,
}: {
  onStart: () => void;
  /** Opens the file picker (live transcription is already off). */
  onUpload: () => void;
  onClose: () => void;
  busy?: boolean;
  /** Start and upload unavailable (e.g. Add recording while the visit is busy). */
  disabled?: boolean;
  /** The heading (default "New visit"; "Add recording" for a visit). */
  title?: string;
  /**
   * Uploads work with live transcription on (Add recording): no offer to
   * switch it off first.
   */
  uploadWithLive?: boolean;
  /** "Patient consented to recording" (VITE_RECORDING_CONSENT_ENABLED). */
  consent?: boolean;
  /** The note template chip (v3.2 recorder: Stop writes the note with it). */
  templateChip?: ReactNode;
  /**
   * The optional visit title, typed in the heading (its placeholder is
   * "New visit", as in the prototype). Without a handler the heading is text.
   */
  visitTitle?: string;
  onVisitTitleChange?: (title: string) => void;
  /** Show the Properties rail when the column is wide (inline desktop). */
  properties?: boolean;
  /** Errors to show above the controls (file validation, recovery). */
  children?: ReactNode;
}): JSX.Element => {
  const { t, i18n } = useTranslation();
  const setup = useRecordingSetup();
  const saveIsRealtimeEnabled = useRecordingSettingsStore(
    (state) => state.saveIsRealtimeEnabled,
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmUpload, setConfirmUpload] = useState(false);
  const uploadHintId = useId();
  const wide = useWide(properties);
  const consented = useRecordingConsentStore((state) => state.consented);
  const now = new Date().toLocaleString(i18n.language || undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

  const options = (
    <>
      <InputLanguageChip setup={setup} />
      <NoteLanguageChip inputLanguage={setup.inputLanguageValue} />
      {templateChip}
      <MicrophoneChip setup={setup} />
    </>
  );

  const upload = (): void => {
    // An upload is transcribed after it arrives; offer to switch live
    // transcription off for it instead of a disabled link.
    if (setup.isRealtimeEnabled && !uploadWithLive) setConfirmUpload(true);
    else onUpload();
  };

  return (
    <div
      className="flex min-h-0 w-full flex-1 flex-col self-stretch bg-v3-panel font-v3 text-v3-text"
      data-testid="newvisit-v32"
    >
      <header className="flex h-14 shrink-0 items-center justify-between px-1.5">
        <V3IconButton label={t("newVisitV32.close")} onClick={onClose}>
          <ChevronLeft className="!size-5" />
        </V3IconButton>
        <p className="text-[15px] font-semibold tabular-nums text-v3-text-2">
          {now}
        </p>
        <V3IconButton
          aria-haspopup="dialog"
          data-testid="newvisit-settings-button"
          label={t("newVisitV32.settings.title")}
          onClick={() => setSettingsOpen(true)}
        >
          <MoreHorizontal className="!size-5" />
        </V3IconButton>
      </header>

      <div className="flex min-h-0 flex-1">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-2">
            <VisitTitleHeading
              placeholder={title ?? t("newVisitV32.title")}
              value={visitTitle}
              onChange={onVisitTitleChange}
            />
            {consent && <RecordingConsentCheck className="mt-4" />}
            <div className="mt-8">
              <div aria-hidden className="space-y-2.5">
                <span className="block h-2 w-2/5 rounded-full bg-v3-surface-3" />
                <span className="block h-2 w-3/5 rounded-full bg-v3-surface-3" />
                <span className="block h-2 w-1/4 rounded-full bg-v3-surface-3" />
              </div>
              <p className="mt-6 text-[17px] leading-[26px] text-v3-text-2">
                {setup.isRealtimeEnabled
                  ? t("newVisitV32.hintLive")
                  : t("newVisitV32.hintDevice")}
              </p>
              <p className="mt-1 text-[14px] leading-5 text-v3-text-3">
                {t("newVisitV32.hintPrivacy")}
              </p>
            </div>
            {children && <div className="mt-6 space-y-3">{children}</div>}
          </div>

          <div className="shrink-0 space-y-3 px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-2 sm:px-4 sm:pb-4">
            {/* The pickers stay right above Start recording at every width
                (clinical lead, 30/10), so they are checked before starting. */}
            <fieldset
              className="mx-auto flex w-full min-w-0 max-w-[540px] flex-wrap items-start gap-2 border-0 px-1"
              data-testid="newvisit-options"
            >
              <legend className="sr-only">{t("newVisitV32.options")}</legend>
              {options}
            </fieldset>
            {/* Ink pill with the sweeping label (as on iOS). */}
            <RecordDock
              busy={busy}
              data-testid="newvisit-start"
              disabled={busy || disabled}
              label={t("newVisitV32.start")}
              onClick={onStart}
            />
            <div className="flex justify-center">
              <V3Button
                aria-describedby={uploadHintId}
                data-testid="newvisit-upload"
                disabled={disabled}
                variant="ghost"
                onClick={upload}
              >
                <Upload />
                {t("newVisitV32.upload")}
              </V3Button>
              <span className="sr-only" id={uploadHintId}>
                {t("error.formRecorder.supportedFormat")}
              </span>
            </div>
          </div>
        </div>
        {wide && (
          <aside
            aria-label={t("newVisitV32.properties.title")}
            className="w-[320px] shrink-0 overflow-y-auto border-l border-v3-border px-6 pb-6 pt-4"
            data-testid="newvisit-properties"
          >
            <h3 className="text-[13px] font-semibold text-v3-text-3">
              {t("newVisitV32.properties.title")}
            </h3>
            <dl className="mt-2">
              <RailRow label={t("newVisitV32.properties.status")}>
                <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-v3-surface-2 px-2.5 text-[13px] font-semibold text-v3-text-2">
                  <span
                    aria-hidden
                    className="size-1.5 rounded-full bg-v3-text-3"
                  />
                  {t("newVisitV32.properties.notStarted")}
                </span>
              </RailRow>
              <RailRow label={t("newVisitV32.properties.date")}>
                <span className="tabular-nums">
                  {new Date().toLocaleString(i18n.language || undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </span>
              </RailRow>
              {consent && (
                <RailRow label={t("newVisitV32.properties.consent")}>
                  {consented
                    ? t("newVisitV32.properties.consentGiven")
                    : t("newVisitV32.properties.consentMissing")}
                </RailRow>
              )}
            </dl>
          </aside>
        )}
      </div>

      <RecordingSettingsDrawer
        open={settingsOpen}
        setup={setup}
        onOpenChange={setSettingsOpen}
      />
      <V3ConfirmSheet
        confirmLabel={t("newVisitV32.uploadSwitch.confirm")}
        description={t("newVisitV32.uploadSwitch.description")}
        open={confirmUpload}
        testId="newvisit-upload-switch"
        title={t("newVisitV32.uploadSwitch.title")}
        onConfirm={() => {
          saveIsRealtimeEnabled(false);
          onUpload();
        }}
        onOpenChange={setConfirmUpload}
      />
    </div>
  );
};
